import React, { useCallback, useMemo, useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import ManualBlock from '../components/manual/ManualBlocks';
import ManualContents, { useActiveSection } from '../components/manual/ManualContents';
import {
  MANUAL_SECTIONS,
  ROLE_LABELS,
  ROLE_SHORT,
  manualGroups,
  searchSections,
  sectionsForRoles,
} from '../common/manual';
import useIsAdmin from '../hooks/useIsAdmin';
import useIsSuperAdmin from '../hooks/useIsSuperAdmin';
import useIsQAAgent from '../hooks/useIsQAAgent';
import useIsCallAgent from '../hooks/useIsCallAgent';

/*
 * The user manual, in the app. Content is data (common/manual); this page is the
 * reading experience around it — role filter, search, contents rail.
 *
 * "For my role" is the default because most readers want the pages they can actually
 * open. Admins writing training material can switch to the whole thing.
 */

const ROLE_ORDER = ['sAdm', 'adm', 'qa', 'callAgent'];

function RoleChips({ roles }) {
  return (
    <div className="flex flex-wrap gap-1">
      {ROLE_ORDER.map((role) => {
        const applies = roles.includes(role);
        return (
          <span
            key={role}
            title={ROLE_LABELS[role]}
            className={`rounded border px-1.5 py-0.5 font-mono text-[10px] tracking-wide ${
              applies
                ? 'border-cloudza-300 bg-cloudza-50 text-cloudza-700'
                : 'border-gray-200 text-gray-400'
            }`}
          >
            {ROLE_SHORT[role]}
          </span>
        );
      })}
    </div>
  );
}

const UserManual = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const isAdmin = useIsAdmin();
  const isQAAgent = useIsQAAgent();
  const isCallAgent = useIsCallAgent();

  const myRoles = useMemo(() => {
    const roles = [];
    if (isSuperAdmin) roles.push('sAdm');
    if (isAdmin) roles.push('adm');
    if (isQAAgent) roles.push('qa');
    if (isCallAgent) roles.push('callAgent');
    return roles;
  }, [isSuperAdmin, isAdmin, isQAAgent, isCallAgent]);

  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');

  const sections = useMemo(() => {
    const scoped = showAll ? MANUAL_SECTIONS : sectionsForRoles(myRoles);
    return searchSections(query, scoped);
  }, [showAll, myRoles, query]);

  const groups = useMemo(() => manualGroups(sections), [sections]);
  const ids = useMemo(() => sections.map((s) => s.id), [sections]);
  const { active, anchorRef } = useActiveSection(ids);

  const goToSection = useCallback((id) => {
    const el = document.getElementById(`manual-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const hiddenByRole = MANUAL_SECTIONS.length - sectionsForRoles(myRoles).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* No printable PDF yet: the HL line's PDF describes that version, not this one. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Manual</h1>
          <p className="mt-1 text-gray-600">
            A walkthrough of the whole tool, from signing in to resolving a disputed assessment.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
          <input
            id="manual-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the manual"
            aria-label="Search the manual"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 placeholder-gray-400 focus:border-cloudza-500 focus:outline-none focus:ring-1 focus:ring-cloudza-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
            >
              <FaTimes className="text-xs" />
            </button>
          )}
        </div>

        <div className="flex rounded-lg border border-gray-300 bg-white p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setShowAll(false)}
            aria-pressed={!showAll}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              !showAll ? 'bg-cloudza-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            For my role
          </button>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            aria-pressed={showAll}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              showAll ? 'bg-cloudza-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Everything
          </button>
        </div>

        <span className="text-sm text-gray-500">
          {sections.length} section{sections.length === 1 ? '' : 's'}
          {!showAll && hiddenByRole > 0 && ` · ${hiddenByRole} hidden by role`}
        </span>
      </div>

      {/* Body */}
      <div className="flex gap-8">
        <aside className="sticky top-0 hidden w-56 flex-shrink-0 self-start py-1 lg:block">
          <ManualContents
            groups={groups}
            active={active}
            onSelect={goToSection}
            anchorRef={anchorRef}
          />
        </aside>

        <div className="min-w-0 flex-1 space-y-8">
          {sections.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
              <p className="text-gray-600">
                Nothing matches &ldquo;{query}&rdquo;.
              </p>
              {!showAll && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="mt-2 text-sm font-medium text-cloudza-600 hover:text-cloudza-700"
                >
                  Search every section, not just my role
                </button>
              )}
            </div>
          )}

          {sections.map((section) => (
            <section
              key={section.id}
              id={`manual-${section.id}`}
              className="scroll-mt-4 rounded-lg border border-gray-200 bg-white p-6 sm:p-8"
            >
              <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                  {section.lede && (
                    <p className="mt-1 text-sm text-gray-500">{section.lede}</p>
                  )}
                </div>
                <RoleChips roles={section.roles} />
              </div>

              {section.blocks.map((block, i) => (
                <ManualBlock key={i} block={block} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserManual;
